/*
  Complementary product popup (product page "Pairs well with", cart drawer, cart page).
  Clicking a row's image or name opens the product here instead of leaving the page. The
  content comes from sections/upsell-quickview.liquid.
  - On the product page with the bundle on: "Add to bundle" copies the chosen variant and
    quantity into the row and ticks it; "Add bundle to cart" does that and adds the bundle.
  - Everywhere else: "Add to cart" adds the chosen variant and quantity.
*/
if (!window.MUpsellQuickview) {
  class MUpsellQuickview {
    constructor() {
      this.cache = new Map();
      this.el = document.createElement('div');
      this.el.className = 'm-uqv';
      this.el.hidden = true;
      this.el.innerHTML = `
        <div class="m-uqv__backdrop" data-uqv-close></div>
        <div class="m-uqv__dialog" role="dialog" aria-modal="true" aria-label="Product details" tabindex="-1">
          <button type="button" class="m-uqv__close" data-uqv-close aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
          </button>
          <div class="m-uqv__body" data-uqv-body></div>
        </div>`;
      document.body.appendChild(this.el);
      this.dialog = this.el.querySelector('.m-uqv__dialog');
      this.body = this.el.querySelector('[data-uqv-body]');

      this.el.addEventListener('click', this.onClick);
      this.el.addEventListener('change', this.onChange);
      // Capture phase, so Escape closes only the popup and not the cart drawer behind it.
      window.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        event.stopPropagation();
        this.close();
      }, true);
    }

    get root() {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    get isCartPage() {
      return window.DeliSettings && DeliSettings.templateName === 'cart';
    }

    // ---------- Open / close ----------

    async open(trigger) {
      const row = trigger.closest('[data-upsell-item]');
      const handle = trigger.dataset.productHandle;
      if (!row || !handle) return;

      this.row = row;
      this.trigger = trigger;
      const bundle = row.closest('m-comp-bundle');
      this.bundle = bundle && bundle.dataset.enabled !== 'false' && row.querySelector('[data-bundle-pick]') ? bundle : null;

      const select = row.querySelector('[data-upsell-variant]');
      const addButton = row.querySelector('[data-upsell-add]');
      const variantId = (select && select.value) || (addButton && addButton.dataset.variantId) || '';
      const rowQty = row.querySelector('[data-upsell-qty]');
      this.startQty = Math.max(1, parseInt(rowQty && rowQty.value, 10) || 1);

      const token = Symbol('load');
      this.loading = token;
      this.body.innerHTML = '<div class="m-uqv__loading"><span class="m-uqv__btn-spinner"></span></div>';
      this.show();

      try {
        const url = `${this.root}products/${encodeURIComponent(handle)}?section_id=upsell-quickview${variantId ? `&variant=${variantId}` : ''}`;
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Could not load ${handle}`);
          html = await response.text();
          this.cache.set(url, html);
        }
        if (this.loading !== token) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-uqv-content]');
        if (!content) throw new Error('Quick view content missing');
        this.body.innerHTML = '';
        this.body.appendChild(content);
        this.setup(content);
      } catch (error) {
        console.error(error);
        if (this.loading !== token) return;
        // Could not load the popup: fall back to the product page.
        window.location.href = trigger.href;
      }
    }

    show() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.el.hidden = false;
      document.documentElement.classList.add('m-uqv-lock');
      requestAnimationFrame(() => {
        this.el.classList.add('is-open');
        this.dialog.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.loading = null;
      this.el.classList.remove('is-open');
      document.documentElement.classList.remove('m-uqv-lock');
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (this.isOpen) return;
        this.el.hidden = true;
        this.body.innerHTML = '';
      }, 280);
      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    // ---------- Popup content ----------

    setup(content) {
      this.content = content;
      this.data = JSON.parse(content.querySelector('[data-uqv-json]').textContent);
      const selected = this.data.variants.find((v) => v.id === this.data.selected) || this.data.variants[0];
      this.options = selected ? selected.options.slice() : [];
      this.dialog.setAttribute('aria-label', content.dataset.productTitle || 'Product details');

      this.qtyInput = content.querySelector('[data-uqv-qty]');
      this.qtyInput.value = this.startQty;

      this.primary = content.querySelector('[data-uqv-primary]');
      this.primaryLabel = content.querySelector('[data-uqv-primary-label]');
      this.secondary = content.querySelector('[data-uqv-secondary]');
      this.errorEl = content.querySelector('[data-uqv-error]');
      this.noteEl = content.querySelector('[data-uqv-note]');
      const eyebrow = content.querySelector('[data-uqv-eyebrow]');

      if (this.bundle) {
        const picked = this.row.querySelector('[data-bundle-pick]');
        this.primaryIdle = picked && picked.checked ? 'Update bundle' : 'Add to bundle';
        this.secondary.hidden = false;
        eyebrow.textContent = 'Pairs well with your pick';
        eyebrow.hidden = false;
        this.noteEl.textContent = 'Bundle items are added to the cart together.';
        this.noteEl.hidden = false;
      } else {
        this.primaryIdle = this.primaryLabel.textContent.trim();
      }

      const track = content.querySelector('[data-uqv-track]');
      if (track) {
        track.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.syncThumbs(track), 60);
        }, { passive: true });
      }

      this.render(false);
      const variant = this.currentVariant();
      if (variant && variant.media_id) this.showMedia(variant.media_id, false);
    }

    currentVariant() {
      return this.data.variants.find((v) => v.options.every((value, i) => value === this.options[i])) || null;
    }

    selectValue(index, value) {
      this.options[index] = value;
      if (!this.currentVariant()) {
        // No variant with this exact mix: keep the new value and pick the closest match.
        const candidates = this.data.variants.filter((v) => v.options[index] === value);
        const best = candidates.find((v) => v.available) || candidates[0];
        if (best) this.options = best.options.slice();
      }
      this.render(true);
    }

    render(moveImage) {
      const variant = this.currentVariant();
      const content = this.content;

      content.querySelectorAll('[data-uqv-option]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.uqvOption);
        const label = fieldset.querySelector('[data-uqv-option-value]');
        if (label) label.textContent = this.options[index];
        fieldset.querySelectorAll('[data-uqv-value]').forEach((button) => {
          const value = button.dataset.uqvValue;
          const selected = value === this.options[index];
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          const available = this.data.variants.some(
            (v) => v.available && v.options[index] === value &&
              v.options.every((other, i) => i === index || other === this.options[i])
          );
          button.classList.toggle('is-unavailable', !available);
        });
      });

      const price = content.querySelector('[data-uqv-price]');
      const compare = content.querySelector('[data-uqv-compare]');
      const save = content.querySelector('[data-uqv-save]');
      const stock = content.querySelector('[data-uqv-stock]');
      const stockText = content.querySelector('[data-uqv-stock-text]');

      if (variant) {
        price.textContent = variant.price_html;
        compare.hidden = !variant.compare_html;
        compare.textContent = variant.compare_html || '';
        save.hidden = !variant.save;
        save.textContent = variant.save ? `-${variant.save}%` : '';
      }
      const available = Boolean(variant && variant.available);
      stock.classList.toggle('is-out', !available);
      stockText.textContent = available ? 'In stock' : variant ? this.data.sold_out : this.data.unavailable;

      this.primary.disabled = !available;
      this.primaryLabel.textContent = available ? this.primaryIdle : stockText.textContent;
      if (this.bundle) this.secondary.disabled = !available;
      this.errorEl.hidden = true;

      if (moveImage && variant && variant.media_id) this.showMedia(variant.media_id);
    }

    showMedia(mediaId, smooth = true) {
      const track = this.content.querySelector('[data-uqv-track]');
      const slide = track && track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.setActiveThumb(String(mediaId));
    }

    syncThumbs(track) {
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const slide = track.children[index];
      if (slide) this.setActiveThumb(slide.dataset.mediaId);
    }

    setActiveThumb(mediaId) {
      this.content.querySelectorAll('[data-uqv-thumb]').forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.uqvThumb === mediaId);
      });
    }

    quantity() {
      const value = Math.min(99, Math.max(1, parseInt(this.qtyInput.value, 10) || 1));
      this.qtyInput.value = value;
      return value;
    }

    // ---------- Events ----------

    onClick = (event) => {
      if (event.target.closest('[data-uqv-close]')) {
        this.close();
        return;
      }
      if (!this.content) return;

      const value = event.target.closest('[data-uqv-value]');
      if (value) {
        const fieldset = value.closest('[data-uqv-option]');
        this.selectValue(Number(fieldset.dataset.uqvOption), value.dataset.uqvValue);
        return;
      }
      const thumb = event.target.closest('[data-uqv-thumb]');
      if (thumb) {
        this.showMedia(thumb.dataset.uqvThumb);
        return;
      }
      const step = event.target.closest('[data-uqv-qty-step]');
      if (step) {
        this.qtyInput.value = Math.min(99, Math.max(1, this.quantity() + Number(step.dataset.uqvQtyStep)));
        return;
      }
      if (event.target.closest('[data-uqv-primary]')) {
        if (this.bundle) this.addToBundle();
        else this.addToCart();
        return;
      }
      if (event.target.closest('[data-uqv-secondary]')) this.addBundleToCart();
    };

    onChange = (event) => {
      if (event.target.matches('[data-uqv-qty]')) this.quantity();
    };

    // ---------- Actions ----------

    // Copy the popup's choice into the row, so the bundle total and button pick it up.
    applyToRow() {
      const variant = this.currentVariant();
      if (!variant || !this.row) return false;
      const row = this.row;
      const fire = (el) => el.dispatchEvent(new Event('change', { bubbles: true }));

      const select = row.querySelector('[data-upsell-variant]');
      if (select && select.value !== String(variant.id)) {
        select.value = String(variant.id);
        fire(select);
      }
      const qty = row.querySelector('[data-upsell-qty]');
      if (qty) {
        qty.value = this.quantity();
        fire(qty);
      }
      const box = row.querySelector('[data-bundle-pick]');
      if (box && !box.disabled && !box.checked) {
        box.checked = true;
        fire(box);
      }
      row.classList.remove('is-flash');
      void row.offsetWidth;
      row.classList.add('is-flash');
      setTimeout(() => row.classList.remove('is-flash'), 1600);
      return true;
    }

    addToBundle() {
      if (this.applyToRow()) this.close();
    }

    async addBundleToCart() {
      if (!this.applyToRow() || typeof this.bundle.addBundle !== 'function') return;
      this.setBusy(this.secondary, true);
      await this.bundle.addBundle();
      this.setBusy(this.secondary, false);
      const bundleError = this.bundle.querySelector('[data-comp-error]');
      if (bundleError && !bundleError.hidden) {
        this.showError(bundleError.textContent);
        return;
      }
      this.close();
    }

    async addToCart() {
      const variant = this.currentVariant();
      if (!variant) return;
      this.setBusy(this.primary, true);
      this.errorEl.hidden = true;
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: this.quantity() }] }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        this.close();
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: !this.isCartPage } }));
      } catch (error) {
        console.error(error);
        this.showError(error.message);
      } finally {
        this.setBusy(this.primary, false);
      }
    }

    setBusy(button, busy) {
      button.classList.toggle('is-loading', busy);
      button.disabled = busy || !(this.currentVariant() && this.currentVariant().available);
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  window.MUpsellQuickview = MUpsellQuickview;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-upsell-quickview]');
    if (!trigger) return;
    // Let ctrl/cmd/shift-click open the product page in a new tab as usual.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (!window.upsellQuickview) window.upsellQuickview = new MUpsellQuickview();
    window.upsellQuickview.open(trigger);
  });
}
